import React, { useState } from 'react';
import PlaidAccountTile from './PlaidAccountTile';
import PlaidReauthBanner from './PlaidReauthBanner';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

/**
 * PlaidBanksRefreshSection - collapsible list of per-account Plaid tiles
 * for the user's connected US/UK/EU/CA banks. Direct mirror of
 * MonoBanksRefreshSection so the experience is identical across regions.
 *
 * Renders nothing if there are no Plaid-connected accounts.
 *
 * Surfaces the same reauth/expiring banner as PlaidReauthBanner inside the
 * section header so Plaid users get the SAME urgency cues as Mono users -
 * regardless of which page they're on (Home, Bank Transactions Center, My Banks).
 *
 * Props:
 *   bankAccounts:     array from /api/bank-accounts
 *   plaidConnections: array from /api/plaid/connections (with `health` annotated)
 *   onRefreshComplete: parent callback for re-fetching data after sync
 */
export default function PlaidBanksRefreshSection({
  bankAccounts = [],
  plaidConnections = [],
  onRefreshComplete,
  defaultOpen = false,
  className = '',
}) {
  const plaidAccounts = (bankAccounts || []).filter(
    (a) => a.open_banking_source === 'plaid' || a.source === 'plaid'
  );

  // Mirror NigeriaBankTile semantics - count anything broken/expiring as
  // needing attention so the header pill matches MonoBanksRefreshSection.
  const needsAttention = plaidAccounts.filter(
    (a) => a.needs_attention === true
  );
  const attentionCount = needsAttention.length;
  const [open, setOpen] = useState(defaultOpen || attentionCount > 0);

  if (plaidAccounts.length === 0) return null;
  // iter959 - For non-attention accounts, hide the entire section on Home.
  // Rationale: Plaid pipes are auto-synced; the section is only useful as an
  // urgency channel when the bank session breaks. Users can still access it
  // from Bank Transactions Center or /banks. This mirrors the NG behaviour where a
  // healthy connection needs no CTA on Home.
  if (attentionCount === 0) return null;

  const headerSubtitle =
    attentionCount > 0
      ? `${attentionCount} of ${plaidAccounts.length} ${plaidAccounts.length === 1 ? 'account needs' : 'accounts need'} attention`
      : `${plaidAccounts.length} ${plaidAccounts.length === 1 ? 'account synced automatically' : 'accounts synced automatically'}`;

  return (
    <div
      data-testid="plaid-banks-refresh-section"
      className={`bg-white border ${attentionCount > 0 ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'} rounded-lg mb-3 ${className}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="plaid-refresh-section-toggle"
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="flex flex-col items-start gap-0.5">
          <span className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 ${attentionCount > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
            Connected banks<span className="hidden sm:inline"> &amp; cards &mdash; Refresh &amp; Reconnect</span>
            {attentionCount > 0 && (
              <span
                data-testid="plaid-section-attention-pill"
                className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
              >
                {attentionCount} need{attentionCount === 1 ? 's' : ''} attention
              </span>
            )}
          </span>
          <span className="text-[11px] font-normal text-slate-500 ml-5 sm:ml-6 truncate max-w-full">
            {headerSubtitle}<span className="hidden sm:inline">. Click a tile below to sync transactions or reconnect after a session expires.</span>
          </span>
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-slate-100 pt-2.5 space-y-2.5">
          {/* Top-of-section reauth/expiring banners (same as MyBanks) */}
          <PlaidReauthBanner
            plaidConnections={plaidConnections}
            onReconnected={onRefreshComplete}
          />
          {plaidAccounts.map((acct) => {
            const connection = plaidConnections.find(c =>
              c.bank_account_id === acct.id
              || (c.accounts || []).some(a => a.account_id === acct.id)
            ) || null;
            return (
              <PlaidAccountTile
                key={acct.id}
                bankAccount={acct}
                connection={connection}
                onRefreshed={onRefreshComplete}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
